import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { WholesalerService } from './../../services/wholesaler.service';

import { ToastrService } from 'ngx-toastr';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeeCode } from 'src/app/enums/fee.code';
import { Role } from 'src/app/enums/role';
import { Bank } from 'src/app/model/bank';
import { Enterprise } from 'src/app/model/enterprise';
import { Fees } from 'src/app/model/fees';
import { CommonService } from 'src/app/services/common.service';
import { CommonUtil } from 'src/app/services/common.util';
import { EnterpriseService } from 'src/app/services/enterprise.service';
import { LoginService } from 'src/app/services/login.service';
import { ModalService } from 'src/app/services/modal.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { MessageConstant } from 'src/app/settings/message.constant';

interface EnterpriseSettingsForm {
    kycLevel: FormControl<string>;
    enterpriseStatus: FormControl<string>;
    visibleInPayList: FormControl<boolean>;
    directDistributor: FormControl<boolean>;
    wholesalerEnabled: FormControl<boolean>;
    supplierEnabled: FormControl<boolean>;
    lovcashIntegrated: FormControl<boolean>;
}

@Component({
    selector: 'app-enterprise-settings',
    templateUrl: './enterprise-settings.component.html',
    styleUrls: ['./enterprise-settings.component.css'],
})
export class EnterpriseSettingsComponent implements OnInit {
    destroyRef = inject(DestroyRef);

    @ViewChild('pinInput') pinInput?: ElementRef<HTMLInputElement>;

    // animation flags
    wholesalerShake = false;
    supplierShake = false;

    feeCode = FeeCode;
    display = false;
    displaySuccess = false;
    sMessage = '';
    role = Role;
    enterprise: Enterprise;
    id: string;
    invalidForm = false;
    eMessage = '';
    editEnterprise: boolean;
    statusDropdown: string[] = [];
    kycLevelDropdown: string[] = [];
    enterpriseFeeList: Array<Fees> = [];
    sessionUser$ = this.loginService.sessionUser.asObservable();
    rawApiKey?: string;
    apiKeyWarningModalId = 'api-key-warning-modal-id';
    apiKeyPinModalId = 'api-key-pin-modal-id';
    pin?: string;
    pinError?: string;
    pinConfirmLoading = false;

    form: FormGroup<EnterpriseSettingsForm>;

    constructor(
        private enterpriseService: EnterpriseService,
        public loginService: LoginService,
        private formBuilder: NonNullableFormBuilder,
        @Inject(DOCUMENT) private document: Document,
        private commonService: CommonService,
        private transactionService: TransactionService,
        public commonUtil: CommonUtil,
        private toasterService: ToastrService,
        private wholesalerService: WholesalerService,
        private modalService: ModalService
    ) {
        this.enterprise = new Enterprise();
        this.form = this.formBuilder.group({
            kycLevel: this.formBuilder.control(''),
            enterpriseStatus: this.formBuilder.control(''),
            visibleInPayList: this.formBuilder.control(false),
            directDistributor: this.formBuilder.control(false),
            wholesalerEnabled: this.formBuilder.control(false),
            supplierEnabled: this.formBuilder.control(false),
            lovcashIntegrated: this.formBuilder.control(false),
        });
    }

    ngOnInit() {
        this.commonService.getUserStatus()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                this.statusDropdown = res.data;
            });

        this.commonService.getKycLevel()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                this.kycLevelDropdown = res.data;
            });

        this.sessionUser$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user) => {
                if (user?.enterprise) {
                    this.id = user.enterprise.enterpriseId;
                    this.getData();
                }
            });
    }

    getData() {
        this.rawApiKey = undefined; // once you refresh, raw key is gone

        this.enterpriseService.getEnterpriseProfile(this.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.enterprise = new Enterprise().deserialize(res.data);
                    if (!this.enterprise.bank) {
                        this.enterprise.bank = new Bank();
                    }
                    this.setForm();
                    this.getFeeList();
                },
                error: () => {
                },
            });
    }

    setForm() {
        this.form.controls.kycLevel.setValue(this.enterprise.kycLevel ?? '');
        this.form.controls.enterpriseStatus.setValue(this.enterprise.status ?? '');

        this.form.controls.visibleInPayList.setValue(
            this.enterprise.visibleInPayList ?? false
        );
        this.form.controls.directDistributor.setValue(
            this.enterprise?.supplier?.directDistributor ?? false
        );
        this.form.controls.wholesalerEnabled.setValue(
            this.enterprise.wholesalerEnabled ?? false
        );
        this.form.controls.supplierEnabled.setValue(
            this.enterprise.supplierEnabled ?? false
        );
        this.form.controls.lovcashIntegrated.setValue(
            this.enterprise?.wholesaler?.lovcashIntegrated ?? false
        );
    }

    getFeeList() {
        this.transactionService
            .getCustomFee(this.enterprise.userRoleId, Role.enterprise)
            .subscribe((res) => {
                this.setFee(res.data || []);
            });
    }

    setFee(feeList: Fees[]) {
        this.enterpriseFeeList = [];

        feeList.forEach((fee) => {
            fee.percentage = fee.percentage || 0.0;
            fee.amount = fee.amount || 0.0;
            fee.useAmount = fee.useAmount || false;
            fee.percentageValue = fee.percentage || 0.0;
            fee.amountValue = fee.amount || 0.0;
            fee.useAmountValue = fee.useAmount || false;
            this.enterpriseFeeList.push(fee);
        });
    }

    changeStatus() {
        this.form.patchValue({
            visibleInPayList: !this.isVisible,
        });
    }

    changeDirectDistributor() {
        this.form.patchValue({
            directDistributor: !this.isDirectDistributor,
        });
    }

    changeLovcashIntegrated() {
        this.form.patchValue({
            lovcashIntegrated: !this.isLovcashIntegrated,
        });
    }

    changeIsWholesaler(event: Event) {
        const input = event.target as HTMLInputElement;
        const checked = input.checked;

        if (!checked) {
            this.form.controls.lovcashIntegrated.setValue(false);
        }

        if (checked && this.isSupplier) {
            this.toasterService.error(
                MessageConstant.WHOLESALER_SUPPLIER_EXCLUSIVE_MSG,
                'Invalid Selection'
            );
            this.triggerShake('wholesaler');
            input.checked = false;
            return;
        }

        this.form.controls.wholesalerEnabled.setValue(checked);
    }

    changeIsSupplier(event: Event) {
        const input = event.target as HTMLInputElement;
        const checked = input.checked;

        if (checked && this.isWholesaler) {
            this.toasterService.error(
                MessageConstant.WHOLESALER_SUPPLIER_EXCLUSIVE_MSG,
                'Invalid Selection'
            );
            this.triggerShake('supplier');
            input.checked = false;
            return;
        }

        this.form.controls.supplierEnabled.setValue(checked);
    }

    get isWholesaler(): boolean {
        return this.form.controls.wholesalerEnabled.value;
    }

    get isSupplier(): boolean {
        return this.form.controls.supplierEnabled.value;
    }

    get isVisible(): boolean {
        return this.form.controls.visibleInPayList.value;
    }

    get isDirectDistributor(): boolean {
        return this.form.controls.directDistributor.value;
    }

    get isLovcashIntegrated(): boolean {
        return this.form.controls.lovcashIntegrated.value;
    }

    resetVal(feeList: Fees[]) {
        feeList.forEach((fee) => {
            fee.percentage = fee.percentage || 0.0;
            fee.amount = fee.amount || 0.0;
            fee.useAmount = fee.useAmount || false;
            fee.percentageValue = fee.percentage || 0.0;
            fee.amountValue = fee.amount || 0.0;
            fee.useAmountValue = fee.useAmount || false;
        });
    }

    changeView(event: any, type: string) {
        this.editEnterprise = !this.editEnterprise;
        this.resetVal(this.enterpriseFeeList);
        this.setForm();
    }

    closeModel(val: boolean) {
        this.display = false;
        this.displaySuccess = false;
    }

    get apiKeyDisplayValue(): string {
        if (this.rawApiKey) return this.rawApiKey;
        if (this.apiKeyExists) return '************** (key already set)';
        return 'No key generated';
    }

    get canCopyRawKey(): boolean {
        return !!this.rawApiKey;
    }

    generateApiKey() {
        this.wholesalerService.generateApiKey(this.enterprise?.wholesaler?.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    if (res.success) {
                        this.rawApiKey = res.result?.apiKey ?? '';;

                        if (this.enterprise?.wholesaler) {
                            this.enterprise.wholesaler.apiKeyRotatedAt = res.result?.apiKeyRotatedAt;
                            this.enterprise.wholesaler.apiKeyExists = true;
                        }
                        this.toasterService.success(
                            'API Key generated. Copy it now — it will not be shown again.', 'Successful');
                    } else {
                        this.toasterService.success(res.status.message, 'Error');
                    }
                },
                error: () => { },
            });
    }

    copyApiKey(event?: MouseEvent) {
        event?.preventDefault();
        event?.stopPropagation();

        if (!this.canCopyRawKey) {
            this.toasterService.info('Copy is only available immediately after generating.');
            return;
        }

        const key = this.rawApiKey;
        if (!key) return;

        navigator.clipboard.writeText(key).then(() => {
            this.toasterService.success('API key copied');
        }).catch(() => {
            this.toasterService.error('Could not copy. Please copy manually.');
        });
    }

    openApiKeyWarningModal() {
        this.modalService.open(this.apiKeyWarningModalId);
    }

    closeApiKeyWarningModal() {
        this.modalService.close(this.apiKeyWarningModalId);
    }

    get isPinValid(): boolean {
        return !!this.pin && this.pin.length >= 4;
    }

    get apiKeyExists(): boolean {
        const rotatedAt = this.enterprise?.wholesaler?.apiKeyRotatedAt;
        console.log('apiKeyExists check, rotatedAt:', rotatedAt);
        return rotatedAt !== null && rotatedAt !== undefined;
    }


    onApiKeyActionClick() {
        if (!this.apiKeyExists) {
            // ✅ first-time key → generate immediately
            this.generateApiKey();
        } else {
            // ⚠️ rotation → warn user
            this.openApiKeyWarningModal();
        }
    }

    openApiKeyPinModal() {
        this.pin = '';
        this.pinError = '';
        this.modalService.open(this.apiKeyPinModalId);

        // wait for modal render
        setTimeout(() => this.pinInput?.nativeElement.focus(), 0);
    }

    closeApiKeyPinModal() {
        this.pin = '';
        this.pinError = '';
        this.pinConfirmLoading = false;
        this.modalService.close(this.apiKeyPinModalId);
    }

    authenticate() {
        if (this.pinConfirmLoading || !this.isPinValid) return;

        this.pinConfirmLoading = true;
        this.pinError = '';

        this.loginService.verifyPin({ pin: this.pin }).subscribe({
            next: (res: any) => {
                const ok = res?.success === true && res?.valid === true;

                if (!ok) {
                    console.warn('PIN verification failed:', res?.status?.message);

                    this.pinError = 'The PIN you entered is incorrect.';
                    this.pinConfirmLoading = false;

                    this.pinConfirmLoading = false;
                    return;
                }

                this.pin = '';
                this.closeApiKeyPinModal();
                this.generateApiKey();
                this.pinConfirmLoading = false;
            },
            error: () => {
                this.pinError = 'Authentication failed. Please try again.';
                this.pinConfirmLoading = false;
            },
        });
    }


    submit() {
        if (this.isWholesaler && this.isSupplier) {
            this.display = true;
            this.eMessage = MessageConstant.WHOLESALER_SUPPLIER_EXCLUSIVE_MSG;
            this.toasterService.error(
                MessageConstant.WHOLESALER_SUPPLIER_EXCLUSIVE_MSG,
                'Invalid Selection'
            );
            return;
        }

        this.invalidForm = this.form.invalid;
        if (this.form.invalid) {
            this.display = true;
            this.eMessage = 'Please fill all required fields.';
            return;
        }

        // Ensure Lovcash integration never saved for non-suppliers
        if (!this.isWholesaler && this.isLovcashIntegrated) {
            this.form.controls.lovcashIntegrated.setValue(false);
        }
        this.sendData();

        const payload = this.form.getRawValue(); // fully typed

        this.commonService
            .changeUserProfileSettings(
                this.enterprise.userId,
                this.enterprise.roleId,
                payload,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.getData();
                    this.changeView('', '');
                    this.toasterService.success(
                        MessageConstant.USER_ADMIN_SETTINGS_UPDATE_SUCCESS,
                        'Successful',
                    );
                },
                error: () => {
                },
            });
    }

    sendData() {
        const form = [];

        this.enterpriseFeeList.forEach((fee) => {
            const value = {
                code: fee.code,
                percentage: fee.percentageValue,
                amount: fee.amountValue,
                useAmount: fee.useAmountValue,
                enterpriseRoleId: this.enterprise.userRoleId,
            };
            form.push(value);
        });

        this.transactionService.updateCustomFees(form)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    if (res.success) {
                        this.setFee(res.data || []);
                    } else {
                        this.display = true;
                        this.eMessage = res.status.message;
                    }
                },
                error: () => {
                },
            });
    }

    resendLink() {
        this.loginService.resendCreatePasswordLink(this.enterprise.code)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this.sMessage = MessageConstant.RESEND_CREATE_PASSWORD_LINK_SUCCESS;
                        this.displaySuccess = true;
                    } else {
                        this.eMessage = 'Unable to resend e-mail, Please try again later';
                        this.display = true;
                    }
                },
                error: (error) => {
                    this.eMessage = 'Service unavailable, Please try again later.';
                    this.display = true;
                },
            });
    }

    // helper to trigger a brief shake
    private triggerShake(type: 'wholesaler' | 'supplier') {
        if (type === 'wholesaler') {
            this.wholesalerShake = false;
            // force reflow in case it's already true
            setTimeout(() => (this.wholesalerShake = true));
            setTimeout(() => (this.wholesalerShake = false), 300);
        } else {
            this.supplierShake = false;
            setTimeout(() => (this.supplierShake = true));
            setTimeout(() => (this.supplierShake = false), 300);
        }
    }
}

